import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { pseoAPI } from '../utils/api';
import { SEO } from '../components/SEO';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useRegion } from '../context/RegionContext';
import { 
  ShoppingBag, 
  TrendingUp, 
  ArrowRight, 
  Search,
  Layers,
  AlertCircle
} from 'lucide-react';

/**
 * Brands Directory Page
 * URL: /brands
 * Lists all brands from database, grouped by category
 */
export const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { currentRegion } = useRegion();
  const currentYear = new Date().getFullYear();

  const buildUrl = (path) => {
    if (currentRegion === 'global') return path;
    return `/${currentRegion}${path}`;
  };

  useEffect(() => {
    const fetchBrands = async () => {
      setLoading(true);
      try {
        const response = await pseoAPI.getBrands();
        setBrands(response.data || []);
      } catch (error) {
        console.error('Failed to fetch brands:', error);
        setBrands([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  // Get unique categories
  const categories = ['all', ...new Set(brands.map(b => b.category).filter(Boolean))];

  // Filter brands
  const filteredBrands = brands.filter(brand => {
    const matchesSearch = brand.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         brand.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || brand.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group brands by category for display
  const brandsByCategory = filteredBrands.reduce((acc, brand) => {
    const category = brand.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(brand);
    return acc;
  }, {});

  // Featured brands (is_featured or is_popular)
  const featuredBrands = filteredBrands.filter(b => b.is_featured || b.is_popular);

  const breadcrumbItems = [];

  return (
    <>
      <SEO 
        title={`All Brands - Deals & Promo Codes (${currentYear})`}
        description="Browse all brands and find exclusive deals, promo codes, and discounts. Save on your favorite stores."
        type="website"
      />

      <div className="min-h-screen bg-gray-50" data-testid="brands-page">
        <Breadcrumbs items={breadcrumbItems} currentPage="All Brands" />

        {/* Hero */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-brand-red/20 rounded-2xl flex items-center justify-center">
                <ShoppingBag className="w-8 h-8 text-brand-red" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-playfair font-bold">
                  All Brands
                </h1>
                <p className="text-gray-400 mt-1">
                  {brands.length} brand{brands.length !== 1 ? 's' : ''} with exclusive deals
                </p>
              </div>
            </div>
            
            {/* Search */}
            <div className="mt-8 max-w-xl">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search brands..."
                  className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-red"
                  data-testid="brands-search-input"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Category Filter */}
          {categories.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-8" data-testid="category-filters">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full font-medium smooth-transition ${
                    selectedCategory === category
                      ? 'bg-brand-red text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                  data-testid={`filter-${category}`}
                >
                  {category === 'all' ? 'All Categories' : category}
                </button>
              ))}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-brand-red"></div>
            </div>
          )}

          {/* Empty State */}
          {!loading && brands.length === 0 && (
            <div className="bg-white rounded-2xl p-12 text-center border border-gray-100" data-testid="no-brands-message">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No brands yet</h2>
              <p className="text-gray-600 mb-6">
                Brands will appear here once they are added via the admin panel.
              </p>
              <Link 
                to="/"
                className="inline-flex items-center gap-2 text-brand-red font-semibold hover:gap-3 smooth-transition"
              >
                Go Home <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {/* No Search Results */}
          {!loading && brands.length > 0 && filteredBrands.length === 0 && (
            <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
              <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No brands found</h2>
              <p className="text-gray-600 mb-4">
                No brands match "{searchQuery}" in {selectedCategory === 'all' ? 'any category' : selectedCategory}.
              </p>
              <button
                onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
                className="text-brand-red font-semibold hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Featured Brands */}
          {!loading && featuredBrands.length > 0 && selectedCategory === 'all' && !searchQuery && (
            <section className="mb-12" data-testid="featured-brands-section">
              <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-6 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-brand-red" />
                Featured Brands
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {featuredBrands.slice(0, 6).map((brand) => (
                  <Link
                    key={brand.slug}
                    to={buildUrl(`/brands/${brand.slug}`)}
                    className="group bg-white rounded-xl p-4 hover:shadow-lg smooth-transition border border-gray-100 hover:border-brand-red text-center"
                    data-testid={`featured-brand-${brand.slug}`}
                  >
                    <div 
                      className="w-16 h-16 rounded-xl mx-auto mb-3 flex items-center justify-center text-white font-bold text-2xl group-hover:scale-110 smooth-transition"
                      style={{ backgroundColor: brand.color || '#374151' }}
                    >
                      {brand.logo ? (
                        <img src={brand.logo} alt={brand.name} className="w-10 h-10 object-contain" />
                      ) : (
                        brand.name?.charAt(0)
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-brand-red smooth-transition">
                      {brand.name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                      {brand.short_description || brand.category}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Brands by Category */}
          {!loading && Object.keys(brandsByCategory).length > 0 && (
            <div className="space-y-12">
              {Object.entries(brandsByCategory).map(([category, categoryBrands]) => (
                <section key={category} data-testid={`category-section-${category}`}>
                  <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Layers className="w-6 h-6 text-brand-green" />
                    {category}
                    <span className="text-sm font-normal text-gray-500">
                      ({categoryBrands.length} brand{categoryBrands.length !== 1 ? 's' : ''})
                    </span>
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {categoryBrands.map((brand) => (
                      <Link
                        key={brand.slug}
                        to={buildUrl(`/brands/${brand.slug}`)}
                        className="group bg-white rounded-xl p-4 hover:shadow-md smooth-transition border border-gray-100 hover:border-brand-green"
                        data-testid={`brand-card-${brand.slug}`}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0 group-hover:scale-110 smooth-transition"
                            style={{ backgroundColor: brand.color || '#374151' }}
                          >
                            {brand.logo ? (
                              <img src={brand.logo} alt={brand.name} className="w-8 h-8 object-contain" />
                            ) : (
                              brand.name?.charAt(0)
                            )}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 group-hover:text-brand-green smooth-transition truncate">
                              {brand.name}
                            </h3>
                            <p className="text-xs text-gray-500 truncate">
                              {brand.short_description || 'View deals'}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BrandsPage;
